import { useEffect, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import {
  getColumnVirtualizationState,
  normalizeColumnVirtualizationOverscan,
} from './columnVirtualization';

import type { RefObject } from 'react';
import type { RowData, Table } from '@tanstack/react-table';
import type { DataTableColumnVirtualization, DataTableColumnVirtualizationState } from '../types';

interface UseColumnVirtualizationProps<TData extends RowData> {
  columnVirtualization: DataTableColumnVirtualization;
  table: Table<TData>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
}

/** 중앙 고정 해제 컬럼의 현재 가로 스크롤 렌더 범위와 spacer를 계산 */
export default function useColumnVirtualization<TData extends RowData>(
  props: UseColumnVirtualizationProps<TData>,
): DataTableColumnVirtualizationState | undefined {
  const { columnVirtualization, table, tableContainerRef } = props;
  const centerColumns = table.getCenterVisibleLeafColumns();
  const centerColumnStateKey = centerColumns
    .map(column => `${column.id}:${column.getSize()}`)
    .join('\u0000');
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const hasWarnedUnsupportedHeaders = useRef(false);
  const headerGroups = table.getHeaderGroups();
  const supportsColumnVirtualization =
    headerGroups.length === 1 &&
    headerGroups[0].headers.every(header => !header.isPlaceholder && header.colSpan === 1);
  const virtualizer = useVirtualizer({
    count: supportsColumnVirtualization ? centerColumns.length : 0,
    estimateSize: index => centerColumns[index]?.getSize() ?? 0,
    getScrollElement: () => scrollElement,
    horizontal: true,
    overscan: normalizeColumnVirtualizationOverscan(columnVirtualization.overscan),
  });

  useEffect(() => {
    setScrollElement(tableContainerRef.current);
  }, [tableContainerRef]);

  useEffect(() => {
    if (
      supportsColumnVirtualization ||
      hasWarnedUnsupportedHeaders.current ||
      process.env.NODE_ENV === 'production'
    ) {
      return;
    }

    console.warn(
      '[DataTable] columnVirtualization은 다단 헤더 또는 placeholder 헤더에서 지원되지 않아 전체 컬럼을 렌더링합니다.',
    );
    hasWarnedUnsupportedHeaders.current = true;
  }, [supportsColumnVirtualization]);

  useEffect(() => {
    virtualizer.measure();
  }, [centerColumnStateKey, virtualizer]);

  if (!scrollElement || !supportsColumnVirtualization) {
    return undefined;
  }

  return getColumnVirtualizationState({
    centerColumnIds: centerColumns.map(column => column.id),
    totalSize: virtualizer.getTotalSize(),
    virtualItems: virtualizer.getVirtualItems(),
  });
}
